import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {formatRelativeTime, highlightHtmlContent} from "../../utils";
import ExpandedStackView from './ExpandedStackView';
import CollapsedStackView from './CollapsedStackView';
import './ProjectStack.css';

const ProjectStack = ({
  project,
  stackedJobs,
  expandedProjects,
  toggleProjectExpansion,
  expandedStacks,
  toggleStackExpansion,
  searchTerm,
  hasActiveFilters,
  openAiDialogForJob,
  TASK_STATUSES,
  ALL_TASKS_TAB,
  renderStackedImageIcon,
  handleRowClick,
  handleRowDoubleClick,
  handleAddFromRow,
  selectedRow,
  handleEdit,
  handleDelete,
  handleViewAttachments,
  handleReplyToNote,
  handlePriorityClick,
  handlePriorityMouseEnter,
  handlePriorityMouseLeave,
  handleNoteTextMouseEnter,
  handleNoteTextMouseLeave,
  handleLinkedNoteClick,
  handleLinkedNoteMouseEnter,
  handleLinkedNoteMouseLeave,
  isNoteReply,
  getReplyNoteId,
  isOriginalNoteExists,
  userStatusMap,
  loadingUsers,
  getPriorityValue,
  manuallyUpdatedPriorities,
  shouldShowNotePopup,
  userId,
  tasksByJob,
  loadingTasksByJob,
  expandedTasks,
  setExpandedTasks,           // NEW
  toggleTaskExpansion,
  notesByTask,
  loadingNotesByTask,
  fetchTasksForJob,
  fetchNotesForTask,
  onTaskEdit,
  onTaskDelete,
  onTaskStatusChange,
  onTaskClick,
  refreshTasksForJob,
  onFilterByProject,
  onFilterByWorkspace,
  refreshNotes,
  highlightedTaskId,
  clearHighlightedTask,
  loadMoreTaskNotes,
  taskPagination,
  onOpenEdit,
  onCreateTask,
  onOpenSlideshow,
  activeTabByJob,
  onTabChange,
  assigneeFilters,
  loadingUsersByJob,
  onAssigneeFilter,
  jobUsers,
  jobUsersTask,
  loadingUsersByJobTask,
  clearAssigneeFilter,
  fetchJobPermissionUsers,
  workspaceRole,
  onDenyUserClick,
  fetchJobTaskUsers,
  hideHeader,
}) => {
  const [expandedJob, setExpandedJob] = useState(null);
  const isProjectExpanded = expandedProjects[project.projectId];
  const projectJobs = (stackedJobs || []).filter(job => job && job.projectId === project.projectId);

  // Refs for cleanup tracking
  const prevJobIdRef = useRef(null);
  const hasClearedForCurrentJobRef = useRef(false);

  const getExpandedTaskForJob = (jobId) => {
    const jobTasks = tasksByJob[jobId] || [];
    const expandedTaskId = Object.keys(expandedTasks || {}).find(
      (taskId) =>
        expandedTasks[taskId] &&
        jobTasks.some((t) => t.id.toString() === taskId)
    );
    if (expandedTaskId) {
      return jobTasks.find((t) => t.id.toString() === expandedTaskId);
    }
    return null;
  };

  const expandedJobObject = useMemo(() => {
    if (!expandedStacks) return null;
    return projectJobs.find(job => job && job.jobName && expandedStacks[job.jobName]);
  }, [projectJobs, expandedStacks]);

  const expandedTaskForExpandedJob = expandedJobObject
    ? getExpandedTaskForJob(expandedJobObject.jobId)
    : null;

  useEffect(() => {
    setExpandedJob(expandedJobObject);
  }, [expandedJobObject]);

  // ***** CLEANUP EFFECT – fixes the "jump to notes" bug *****
  useEffect(() => {
    const currentJobId = expandedJobObject?.jobId;
    const prevJobId = prevJobIdRef.current;

    // Case 1: Job expanded (currentJobId present and different from previous)
    if (currentJobId && currentJobId !== prevJobId) {
      // Only clear if we haven't cleared for this specific expansion
      if (!hasClearedForCurrentJobRef.current) {
        setExpandedTasks(prev => {
          const newState = { ...prev };
          const jobTasks = tasksByJob[currentJobId] || [];
          jobTasks.forEach(task => {
            if (newState[task.id]) delete newState[task.id];
          });
          return newState;
        });
        hasClearedForCurrentJobRef.current = true;
      }
    }
    // Case 2: Job collapsed (currentJobId null, prevJobId had a value)
    else if (!currentJobId && prevJobId) {
      // Reset the flag for the next time this job is expanded
      hasClearedForCurrentJobRef.current = false;
      // Optionally clear tasks for the collapsed job as well
      setExpandedTasks(prev => {
        const newState = { ...prev };
        const jobTasks = tasksByJob[prevJobId] || [];
        jobTasks.forEach(task => {
          if (newState[task.id]) delete newState[task.id];
        });
        return newState;
      });
    }

    prevJobIdRef.current = currentJobId;
  }, [expandedJobObject, tasksByJob, setExpandedTasks]);

  const onOpenProjectEdit = (project) => {
    console.log("Opening edit for project:", project);
  };

  const totalNotes = projectJobs.reduce((sum, job) => sum + (job.noteCount || 0), 0);
  const totalJobs = projectJobs.length;
  const latestJob = [...projectJobs].sort((a, b) =>
    new Date(b.latestTimeStamp || 0) - new Date(a.latestTimeStamp || 0)
  )[0];

  // COLLAPSED VIEW
  if (!isProjectExpanded) {
    const maxLayers = Math.min(totalJobs, 5);
    return (
      <div className="ps-collapsed" onClick={() => toggleProjectExpansion(project.projectId)}>
        {[...Array(maxLayers)].map((_, idx) => {
          const index = (maxLayers - 1) - idx;
          const isTopCard = idx === maxLayers - 1;
          return (
            <div
              key={`project-layer-${index}`}
              className="ps-layer"
              style={{
                left: `${index * 15}px`,
                transform: `rotate(${index * -2}deg)`,
                zIndex: maxLayers - index,
                opacity: 1 - index * 0.15,
              }}
            >
              {isTopCard ? (
                <div className="ps-top-card">
                  <div className="ps-card-header">
                    <div className="ps-card-title">
                      <i className="fas fa-folder" />
                      <div>
                        <span>{project.projectName}</span>
                        {project.workspaceName && (
                          <small className="ps-workspace">{project.workspaceName}</small>
                        )}
                      </div>
                    </div>
                    <div className="ps-badge">
                      <i className="fas fa-layer-group" />
                      <span>{totalNotes} notes</span>
                    </div>
                  </div>
                  <div className="ps-card-body">
                    <div className="ps-avatar-row">
                      <div className="ps-avatar">
                        {latestJob?.lastSiteNoteUserName?.charAt(0).toUpperCase() ||
                          project.projectName.charAt(0).toUpperCase()}
                      </div>
                      <span className="ps-username">
                        {latestJob?.lastSiteNoteUserName || "No activity"}
                      </span>
                    </div>
                    <div
                      className="ps-note-preview"
                      dangerouslySetInnerHTML={{
                        __html: highlightHtmlContent(
                          latestJob?.lastSiteNote || "No recent notes",
                          searchTerm
                        ),
                      }}
                    />
                    <div className="ps-footer">
                      <span>
                        <i className="fas fa-clock" />{" "}
                        {latestJob?.latestTimeStamp
                          ? formatRelativeTime(latestJob.latestTimeStamp)
                          : "No updates"}
                      </span>
                      <span>
                        <i className="fas fa-briefcase" /> {totalJobs}{" "}
                        {totalJobs === 1 ? "Job" : "Jobs"}
                      </span>
                    </div>
                  </div>
                  <div className="ps-click-hint">
                    {totalJobs === 1 ? "Click to expand job" : "Click to expand project"}
                  </div>
                </div>
              ) : (
                <div className="ps-placeholder-card">
                  <i className="fas fa-folder" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // EXPANDED VIEW
  return (
    <div className="ps-expanded">
      {!hideHeader && (
        <div className="ps-header">
          <div className="ps-header-left">
            <div className="ps-job-info">
              <i className="fas fa-folder" />
              <div className="ps-job-details">
                <span className="ps-job-name">{project.projectName}</span>
                <small className="ps-meta">
                  {project.workspaceName} • {totalJobs} jobs • {totalNotes} notes
                </small>
              </div>
            </div>
            {expandedJobObject && (
              <>
                <i className="fas fa-chevron-right ps-chevron" />
                <div className="ps-breadcrumb-item">
                  <i className="fas fa-briefcase" />
                  {(expandedJobObject.friendlyId || expandedJobObject.jobId || expandedJobObject.id) && (
                    <span className="expanded-stack-task-id">
                      {expandedJobObject.friendlyId || expandedJobObject.jobId || expandedJobObject.id}
                    </span>
                  )}
                  <span>{expandedJobObject.jobName}</span>
                </div>
              </>
            )}
            {expandedTaskForExpandedJob && (
              <>
                <i className="fas fa-chevron-right ps-chevron" />
                <div className="ps-breadcrumb-item">
                  <i className="fas fa-tasks" />
                  <span>{expandedTaskForExpandedJob.title}</span>
                </div>
              </>
            )}
          </div>
          <div className="ps-actions">
            {expandedJobObject && !expandedTaskForExpandedJob && (
              <div className="ps-badge task-badge">
                <i className="fas fa-tasks" />
                <span>
                  {(tasksByJob[expandedJobObject.jobId] || []).length}{" "}
                  {(tasksByJob[expandedJobObject.jobId] || []).length === 1 ? "Task" : "Tasks"}
                </span>
              </div>
            )}
            <button
              className="ps-action-btn slideshow"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSlideshow(expandedJobObject || projectJobs[0], 0);
              }}
              title="View slideshow"
            >
              <i className="fas fa-images" />
            </button>
            {expandedJobObject && !expandedTaskForExpandedJob && (
              <button
                className="ps-action-btn create-task"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateTask(expandedJobObject);
                }}
                title="Create Task"
              >
                <i className="fas fa-plus-circle" />
              </button>
            )}
            <button
              className="ps-action-btn ai-summarize"
              onClick={(e) => {
                e.stopPropagation();
                openAiDialogForJob(expandedJobObject || projectJobs[0]);
              }}
              title="Summarize notes (AI)"
            >
              <i className="fas fa-comments" />
            </button>
            <button
              className="ps-action-btn settings"
              onClick={(e) => {
                e.stopPropagation();
                onOpenProjectEdit(project);
              }}
              title="Edit Project"
            >
              <i className="fas fa-cog" />
            </button>
            <button
              className="ps-back-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (expandedTaskForExpandedJob) {
                  toggleTaskExpansion(expandedTaskForExpandedJob.id, expandedJobObject.jobId);
                } else if (expandedJobObject) {
                  toggleStackExpansion(expandedJobObject.jobName, expandedJobObject.jobId);
                  setExpandedJob(null);
                } else {
                  toggleProjectExpansion(project.projectId);
                }
              }}
              title="Back"
            >
              <i className="fas fa-arrow-left" />
            </button>
          </div>
        </div>
      )}
      <div className="ps-body">
        <div className="ps-jobs-container">
          {expandedJobObject ? (
            <div className="ps-expanded-job">
              <ExpandedStackView
                job={expandedJobObject}
                searchTerm={searchTerm}
                hasActiveFilters={hasActiveFilters}
                openAiDialogForJob={openAiDialogForJob}
                renderStackedImageIcon={renderStackedImageIcon}
                selectedRow={selectedRow}
                handleRowClick={handleRowClick}
                handleRowDoubleClick={handleRowDoubleClick}
                handleAddFromRow={handleAddFromRow}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleViewAttachments={handleViewAttachments}
                handleReplyToNote={handleReplyToNote}
                handlePriorityClick={handlePriorityClick}
                handlePriorityMouseEnter={handlePriorityMouseEnter}
                handlePriorityMouseLeave={handlePriorityMouseLeave}
                handleNoteTextMouseEnter={handleNoteTextMouseEnter}
                handleNoteTextMouseLeave={handleNoteTextMouseLeave}
                handleLinkedNoteClick={handleLinkedNoteClick}
                handleLinkedNoteMouseEnter={handleLinkedNoteMouseEnter}
                handleLinkedNoteMouseLeave={handleLinkedNoteMouseLeave}
                isNoteReply={isNoteReply}
                getReplyNoteId={getReplyNoteId}
                isOriginalNoteExists={isOriginalNoteExists}
                userStatusMap={userStatusMap}
                loadingUsers={loadingUsers}
                getPriorityValue={getPriorityValue}
                manuallyUpdatedPriorities={manuallyUpdatedPriorities}
                shouldShowNotePopup={shouldShowNotePopup}
                toggleStackExpansion={toggleStackExpansion}
                userId={userId}
                onOpenEdit={onOpenEdit}
                onCreateTask={onCreateTask}
                onOpenSlideshow={onOpenSlideshow}
                tasks={tasksByJob[expandedJobObject.jobId] || []}
                loadingTasks={loadingTasksByJob[expandedJobObject.jobId] || false}
                expandedTasks={expandedTasks}
                expandedTask={expandedTaskForExpandedJob}
                fetchTasksForJob={fetchTasksForJob}
                isAnyStackExpanded={Object.values(expandedStacks || {}).some(v => v)}
                hasAnyTaskExpanded={Object.values(expandedTasks || {}).some(v => v)}
                expandedStacks={expandedStacks}
                stackedJobs={projectJobs}
                onTaskToggle={(taskId) => {
                  let foundJobId = null;
                  for (const [jId, tasks] of Object.entries(tasksByJob)) {
                    if (tasks.some((t) => t.id === taskId)) {
                      foundJobId = parseInt(jId);
                      break;
                    }
                  }
                  if (foundJobId && toggleTaskExpansion) {
                    toggleTaskExpansion(taskId, foundJobId);
                  }
                }}
                onTaskClick={onTaskClick}
                onTaskEdit={onTaskEdit}
                onTaskDelete={onTaskDelete}
                onTaskStatusChange={onTaskStatusChange}
                notesByTask={notesByTask}
                loadingNotesByTask={loadingNotesByTask}
                onCollapseTask={() => {
                  if (expandedTaskForExpandedJob) {
                    toggleTaskExpansion(expandedTaskForExpandedJob.id, expandedJobObject.jobId);
                  }
                }}
                activeTab={activeTabByJob && activeTabByJob[expandedJobObject.jobId] || 0}
                onTabChange={onTabChange}
                TASK_STATUSES={TASK_STATUSES}
                ALL_TASKS_TAB={ALL_TASKS_TAB}
                highlightedTaskId={highlightedTaskId}
                assigneeFilters={assigneeFilters}
                jobUsers={jobUsersTask}
                loadingUsersByJob={loadingUsersByJobTask}
                onAssigneeFilter={onAssigneeFilter}
                clearAssigneeFilter={clearAssigneeFilter}
                fetchNotesForTask={fetchNotesForTask}
                loadMoreTaskNotes={loadMoreTaskNotes}
                taskPagination={taskPagination}
                workspaceRole={workspaceRole}
                fetchJobPermissionUsers={fetchJobTaskUsers}
                refreshNotes={refreshNotes}
              />
            </div>
          ) : (
            projectJobs.map((job) => (
              <div key={`collapsed-${job.jobId}`} className="ps-job-wrapper">
                <CollapsedStackView
                  job={job}
                  searchTerm={searchTerm}
                  hasActiveFilters={hasActiveFilters}
                  openAiDialogForJob={openAiDialogForJob}
                  toggleStackExpansion={toggleStackExpansion}
                  onOpenEdit={onOpenEdit}
                  onCreateTask={onCreateTask}
                  onOpenSlideshow={onOpenSlideshow}
                  onFilterByProject={onFilterByProject}
                  onFilterByWorkspace={onFilterByWorkspace}
                  jobUsers={jobUsers}
                  loadingUsersByJob={loadingUsersByJob}
                  fetchJobPermissionUsers={fetchJobPermissionUsers}
                  workspaceRole={workspaceRole}
                  onDenyUserClick={onDenyUserClick}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

ProjectStack.propTypes = {
  project: PropTypes.object.isRequired,
  stackedJobs: PropTypes.array,
  expandedProjects: PropTypes.object.isRequired,
  toggleProjectExpansion: PropTypes.func.isRequired,
  expandedStacks: PropTypes.object,
  toggleStackExpansion: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  hasActiveFilters: PropTypes.bool,
  openAiDialogForJob: PropTypes.func.isRequired,
  renderStackedImageIcon: PropTypes.func,
  selectedRow: PropTypes.any,
  handleRowClick: PropTypes.func,
  handleRowDoubleClick: PropTypes.func,
  handleAddFromRow: PropTypes.func,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  handleViewAttachments: PropTypes.func,
  handleReplyToNote: PropTypes.func,
  handlePriorityClick: PropTypes.func,
  handlePriorityMouseEnter: PropTypes.func,
  handlePriorityMouseLeave: PropTypes.func,
  handleNoteTextMouseEnter: PropTypes.func,
  handleNoteTextMouseLeave: PropTypes.func,
  handleLinkedNoteClick: PropTypes.func,
  handleLinkedNoteMouseEnter: PropTypes.func,
  handleLinkedNoteMouseLeave: PropTypes.func,
  isNoteReply: PropTypes.func,
  getReplyNoteId: PropTypes.func,
  isOriginalNoteExists: PropTypes.func,
  userStatusMap: PropTypes.object,
  loadingUsers: PropTypes.object,
  getPriorityValue: PropTypes.func,
  manuallyUpdatedPriorities: PropTypes.object,
  shouldShowNotePopup: PropTypes.func,
  userId: PropTypes.number,
  tasksByJob: PropTypes.object,
  loadingTasksByJob: PropTypes.object,
  expandedTasks: PropTypes.object,
  toggleTaskExpansion: PropTypes.func,
  notesByTask: PropTypes.object,
  loadingNotesByTask: PropTypes.object,
  fetchTasksForJob: PropTypes.func,
  fetchNotesForTask: PropTypes.func,
  onTaskEdit: PropTypes.func,
  onTaskDelete: PropTypes.func,
  onTaskStatusChange: PropTypes.func,
  onTaskClick: PropTypes.func,
  refreshTasksForJob: PropTypes.func,
  onFilterByProject: PropTypes.func,
  onFilterByWorkspace: PropTypes.func,
  refreshNotes: PropTypes.func,
  highlightedTaskId: PropTypes.number,
  clearHighlightedTask: PropTypes.func,
  loadMoreTaskNotes: PropTypes.func,
  taskPagination: PropTypes.object,
  onOpenEdit: PropTypes.func,
  onCreateTask: PropTypes.func,
  onOpenSlideshow: PropTypes.func,
  activeTabByJob: PropTypes.object,
  onTabChange: PropTypes.func,
  assigneeFilters: PropTypes.object,
  loadingUsersByJob: PropTypes.object,
  onAssigneeFilter: PropTypes.func,
  jobUsers: PropTypes.object,
  jobUsersTask: PropTypes.object,
  loadingUsersByJobTask: PropTypes.object,
  clearAssigneeFilter: PropTypes.func,
  fetchJobPermissionUsers: PropTypes.func,
  workspaceRole: PropTypes.string,
  onDenyUserClick: PropTypes.func,
  fetchJobTaskUsers: PropTypes.func,
  hideHeader: PropTypes.bool,
  setExpandedTasks: PropTypes.func,
};

ProjectStack.defaultProps = {
  stackedJobs: [],
  expandedStacks: {},
  tasksByJob: {},
  loadingTasksByJob: {},
  expandedTasks: {},
  notesByTask: {},
  loadingNotesByTask: {},
  assigneeFilters: {},
  loadingUsersByJob: {},
  jobUsers: {},
  jobUsersTask: {},
  loadingUsersByJobTask: {},
  activeTabByJob: {},
  taskPagination: {},
  searchTerm: '',
  hasActiveFilters: false,
  renderStackedImageIcon: () => null,
  selectedRow: null,
  handleRowClick: () => {},
  handleRowDoubleClick: () => {},
  handleAddFromRow: () => {},
  handleEdit: () => {},
  handleDelete: () => {},
  handleViewAttachments: () => {},
  handleReplyToNote: () => {},
  handlePriorityClick: () => {},
  handlePriorityMouseEnter: () => {},
  handlePriorityMouseLeave: () => {},
  handleNoteTextMouseEnter: () => {},
  handleNoteTextMouseLeave: () => {},
  handleLinkedNoteClick: () => {},
  handleLinkedNoteMouseEnter: () => {},
  handleLinkedNoteMouseLeave: () => {},
  isNoteReply: () => false,
  getReplyNoteId: () => null,
  isOriginalNoteExists: () => false,
  userStatusMap: {},
  loadingUsers: {},
  getPriorityValue: () => null,
  manuallyUpdatedPriorities: {},
  shouldShowNotePopup: () => false,
  userId: null,
  fetchTasksForJob: () => {},
  fetchNotesForTask: () => {},
  onTaskEdit: () => {},
  onTaskDelete: () => {},
  onTaskStatusChange: () => {},
  onTaskClick: () => {},
  refreshTasksForJob: () => {},
  onFilterByProject: () => {},
  onFilterByWorkspace: () => {},
  refreshNotes: () => {},
  highlightedTaskId: null,
  clearHighlightedTask: () => {},
  loadMoreTaskNotes: () => {},
  taskPagination: {},
  onOpenEdit: () => {},
  onCreateTask: () => {},
  onOpenSlideshow: () => {},
  onTabChange: () => {},
  onAssigneeFilter: () => {},
  clearAssigneeFilter: () => {},
  fetchJobPermissionUsers: () => {},
  workspaceRole: null,
  onDenyUserClick: () => {},
  fetchJobTaskUsers: () => {},
  hideHeader: false,
  setExpandedTasks: () => {},
};

export default ProjectStack;

